import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import { type FactGroup } from '@/karehero-app/tell-us-once/intake/FactList';
import { type ReviewRow } from '@/karehero-app/tell-us-once/intake/content';

// The voice agent's Health stage (AA1 questions 16 to 23), opened with what we
// already hold rather than closed with a read-back. The rows are FactList's, so
// each one edits in a drawer. No source line under an answer; the sparkle
// marks what was read from an upload. No read-back at the
// end; they have watched it fill. Margaret is from the sample call in the voice
// agent spec.

const gap = (id: string, label: string): ReviewRow => ({
  id,
  label,
  value: '',
  from: 'missing',
});

// Only what we hold: this is confirming, not a form, so a gap isn't shown. Rows
// appear as the conversation fills them. One list with no headings: it is a
// handful of answers, not sections of a form.
const health = (filled: Record<string, Partial<ReviewRow>>): FactGroup[] => {
  const row = (base: ReviewRow) => ({ ...base, ...filled[base.id] });
  const rows = [
    row({
      id: 'conditions',
      label: 'Conditions',
      value: 'Arthritis, early dementia',
    }),
    row(gap('how-long', 'How long Margaret has had them')),
    row(gap('medicines', 'Medicines and treatments')),
    row({ id: 'gp', label: 'GP', value: 'Elm Road surgery' }),
    row({
      id: 'helps',
      label: 'Who helps',
      value: 'You, with meals and medication',
    }),
    row(gap('others', 'Anyone else Margaret sees')),
    row(gap('reports', 'Reports')),
    row(gap('surgery', 'Waiting for surgery')),
    row(gap('tests', 'Tests')),
  ];
  return [{ label: '', rows: rows.filter((r) => r.value) }];
};

const TALKED = {
  conditions: { value: 'Arthritis, early dementia, angina' },
  'how-long': {
    value: 'Arthritis about 10 years, dementia since spring 2026',
    from: undefined,
  },
  medicines: {
    value: 'Co-codamol, donepezil, GTN spray',
    from: 'ai' as const,
  },
  others: {
    value: "Dr Okafor, St Mary's memory clinic",
    from: 'ai' as const,
  },
};

export const HEALTH_START: ChatQuestion = {
  id: 'health-start',
  says: ["Next, Margaret's health. We've got a few bits already."],
  facts: health({}),
  ask: 'Can you check these are right?',
  mode: 'say',
};

export const HEALTH_START_ANSWER = "Mum's got angina as well";

export const HEALTH_MID: ChatQuestion = {
  id: 'health-mid',
  says: [
    "Thanks. I've got the medicines from your photo and Dr Okafor from the clinic letter.",
  ],
  facts: health(TALKED),
  ask: 'Last one. Does Margaret have any reports or test results, or a date for surgery?',
  mode: 'say',
};

export const HEALTH_MID_ANSWER = 'No, none of those';

// The variant: what we hold as a card from us, Label: value a line, the way
// the pet profile reads it back. Shorter than the rows; a correction is
// said, or the pencil opens the same rows as above on a page, each editing in a
// drawer. Only the opening lists anything; after that the agent confirms.
export const HEALTH_HELD = health({});
export const HEALTH_CARD_LABEL = "Margaret's health";

export const HEALTH_START_BULLETS: ChatQuestion = {
  id: 'health-start',
  says: ["Next, Margaret's health. We've got a few bits already."],
  card: {
    label: HEALTH_CARD_LABEL,
    sentence: true,
    editable: true,
    items: HEALTH_HELD[0].rows.map((r) => `${r.label}: ${r.value}`),
    facts: HEALTH_HELD,
  },
  ask: 'Can you check these are right?',
  mode: 'say',
};

export const HEALTH_MID_BULLETS: ChatQuestion = {
  ...HEALTH_MID,
  facts: undefined,
};

export const HEALTH_DONE_BULLETS: ChatQuestion = {
  id: 'health-done',
  ask: "That's Margaret's health done. Next, where Margaret lives.",
  mode: 'say',
};

export const HEALTH_DONE: ChatQuestion = {
  id: 'health-done',
  facts: health({
    ...TALKED,
    reports: { value: 'None', from: undefined },
    surgery: { value: 'No', from: undefined },
    tests: { value: 'None', from: undefined },
  }),
  ask: "That's Margaret's health done. Next, where Margaret lives.",
  mode: 'say',
};
